import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { ArrowLeft, Calendar, Play } from "lucide-react";
import { merchantsService } from "@/services/merchants";
import { courtsService } from "@/services/courts";
import { videoService } from "@/services/videos";
import { format, parse, isValid } from "date-fns";

interface VideoSlot {
  id: number;
  court_id?: string;
  storage_url?: string;
  url?: string;
  watch_count?: number;
  price: number;
  purchase_count?: boolean;
  download_count?: number;
  created_at?: string;
  updated_at?: string;
}

const Videos = () => {
  const { brandSlug, courtSlug } = useParams();
  const brandId = brandSlug?.split("-").pop();
  const courtId = courtSlug?.split("-").pop();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState<VideoSlot[]>([]);

  // this is the date that actually drives the API request
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // modal open/close + the value being edited inside the modal
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(selectedDate);
  const [draftDateInput, setDraftDateInput] = useState(selectedDate);

  const [courtName, setCourtName] = useState("");
  const [brandName, setBrandName] = useState("");

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 10;

  // synchronise page param with URL
  useEffect(() => {
    const pageParam = parseInt(searchParams.get("page") || "1");
    if (!isNaN(pageParam) && pageParam > 0) {
      setCurrentPage(pageParam);
    }
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem("selectedDate", selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    // when the date or court changes we want to go back to page 1
    setCurrentPage(1);
    setSearchParams({ page: "1" });
  }, [selectedDate, courtId]);

  useEffect(() => {
    //get merchant name
    merchantsService
      .fetchMerchants({ id: brandId })
      .then((data) => {
        const brand = data.merchants.find((b) => b.id == brandId);
        if (brand) {
          setBrandName(brand.name);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch brand name:", error);
      });

    //get court name
    courtsService
      .fetchCourtById(courtId || "")
      .then((data) => {
        const res = data.court;
        if (res) {
          setCourtName(res.name);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch court name:", error);
      });

    //get videos by court id with pagination
    if (currentPage === 1) {
      setIsLoading(true); // full skeleton only first load
    }
    videoService
      .fetchVideosByCourtId(courtId, selectedDate, selectedDate, currentPage, limit)
      .then((data) => {
        const formattedVideos = data.clips.map((video) => ({
          id: parseInt(video.id),
          hour: video.created_at,
          price: parseInt(video.price),
          url: video.url,
          date: video.date,
        }));
        setVideos(formattedVideos);
        if (data.pagination && data.pagination.total !== undefined) {
          setTotalCount(data.pagination.total);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch videos:", error);
      })
      .finally(() => {
        setTimeout(() => setIsLoading(false), 300);
      });
  }, [brandId, courtId, selectedDate, currentPage]);

  function getTimeRangeFromUrl(url: string): string {
    // Extract timestamp from URL
    const match = url.match(/(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}\.\d+)/);

    if (!match) return "";

    const rawTime = match[1];
    // example: 2026-03-06_14-52-29.677

    // Convert to ISO format
    const formatted = rawTime
      .replace("_", "T") // 2026-03-06T14-52-29.677
      .replace(/-/g, ":") // 2026:03:06T14:52:29.677
      .replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3");
    // fix date part back

    const date = new Date(formatted);

    // subtract 30 seconds
    const earlier = new Date(date.getTime() - 30 * 1000);

    // format HH:mm:ss
    const formatTime = (d: Date) => d.toTimeString().split(" ")[0];

    return `${formatTime(earlier)} - ${formatTime(date)}`;
  }

  function getDate(dateTimeStr: string) {
    // Convert string to Date
    const date = dateTimeStr.split(" ")[0];
    return date;
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleVideoSelect = (videoId: number) => {
    navigate(`/videos/${videoId}`);
  };

  // ---- date modal helpers ----

  const openDateModal = () => {
    // seed the draft with whatever is currently applied
    setDraftDate(selectedDate);
    setDraftDateInput(selectedDate);
    setIsDateModalOpen(true);
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) return;
    const iso = format(date, "yyyy-MM-dd");
    setDraftDate(iso);
    setDraftDateInput(iso);
  };

  const handleManualInputChange = (value: string) => {
    setDraftDateInput(value);
    // keep the calendar in sync only once it's a fully valid date
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    if (isValid(parsed) && value.length === 10) {
      setDraftDate(value);
    }
  };

  const handleApplyDate = () => {
    // validate the manual input one more time before committing
    const parsed = parse(draftDateInput, "yyyy-MM-dd", new Date());
    const finalDate = isValid(parsed) ? draftDateInput : draftDate;

    setSelectedDate(finalDate); // <- this is what actually triggers the API fetch
    setIsDateModalOpen(false);
  };

  const handleCancelDate = () => {
    // discard whatever was chosen in the modal
    setDraftDate(selectedDate);
    setDraftDateInput(selectedDate);
    setIsDateModalOpen(false);
  };

  const displayDate = (() => {
    const parsed = parse(selectedDate, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? format(parsed, "EEEE, d MMMM yyyy") : selectedDate;
  })();

  const draftDateAsDate = (() => {
    const parsed = parse(draftDate, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : undefined;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100">
      <main className="max-w-6xl mx-auto px-4 py-10">
        {/* HEADER */}
        <div className="mb-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const slug = brandName.toLowerCase().replace(/\s+/g, "_");
              navigate(`/brands/${slug}-${brandId}/courts`);
            }}
            className="mb-6 hover:bg-white/60 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Courts
          </Button>

          <h1 className="text-4xl font-bold tracking-tight">
            {isLoading ? <Skeleton className="h-10 w-[250px]" /> : courtName}
          </h1>

          <p className="text-lg text-muted-foreground mt-1 flex items-center gap-2">
            at{" "}
            {isLoading ? (
              <Skeleton className="h-5 w-[150px]" />
            ) : (
              <span className="font-semibold">{brandName}</span>
            )}
          </p>
        </div>

        {/* DATE SELECTOR */}
        <div className="mb-10 bg-white/70 backdrop-blur-md p-6 rounded-2xl shadow-sm border">
          <span className="flex items-center gap-2 text-sm font-semibold mb-3">
            <Calendar className="w-4 h-4" />
            Select Date
          </span>

          <Button
            variant="outline"
            onClick={openDateModal}
            className="max-w-xs w-full justify-start bg-white font-normal"
          >
            <Calendar className="w-4 h-4 mr-2" />
            {displayDate}
          </Button>
        </div>

        {/* DATE MODAL */}
        <Dialog open={isDateModalOpen} onOpenChange={setIsDateModalOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle>Select a date</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex justify-center">
                <CalendarPicker
                  mode="single"
                  selected={draftDateAsDate}
                  onSelect={handleCalendarSelect}
                />
              </div>

              <div>
                <label
                  htmlFor="manual-date"
                  className="text-sm font-medium mb-2 block"
                >
                  Or type a date
                </label>
                <Input
                  id="manual-date"
                  type="date"
                  value={draftDateInput}
                  onChange={(e) => handleManualInputChange(e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancelDate}>
                Cancel
              </Button>
              <Button onClick={handleApplyDate}>OK</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* VIDEO GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {isLoading && videos.length === 0 ? (
            Array.from({ length: limit }).map((_, i) => (
              <Card key={`skeleton-${i}`} className="overflow-hidden rounded-2xl">
                <div className="relative aspect-video w-full overflow-hidden rounded-t-2xl bg-gray-200">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                </div>
                <CardContent className="p-5 space-y-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-28" />
                </CardContent>
              </Card>
            ))
          ) : !isLoading && videos.length > 0 ? (
            videos.map((video) => (
              <Card
                key={video.id}
                className="group cursor-pointer rounded-2xl overflow-hidden border bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-xl transition-all duration-300"
                onClick={() => !isLoading && handleVideoSelect(video.id)}
              >
                <div className="relative aspect-video overflow-hidden">
                  <video
                    src={video.url}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    muted
                    loop
                  />

                  {/* gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center">
                    <Play className="w-14 h-14 text-white opacity-90 group-hover:scale-110 transition-transform" />
                  </div>
                </div>

                <CardContent className="p-5">
                  <h3 className="font-semibold text-lg mb-3">
                    {getTimeRangeFromUrl(video.url.toString())}
                  </h3>

                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Date</span>
                      <span className="font-medium text-foreground">
                        {getDate(video.hour)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quality</span>
                      <span className="font-medium text-foreground">HD 1080p</span>
                    </div>
                  </div>

                  <Button className="w-full mt-5 rounded-xl">See Preview</Button>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-20 bg-white/60 rounded-2xl border shadow-sm">
              <p className="text-xl font-semibold mb-2">No videos available</p>
              <p className="text-muted-foreground">Try selecting a different date</p>
            </div>
          )}
        </div>

        {/* PAGINATION */}
        {totalCount > limit && (
          <div className="flex justify-center items-center gap-6 mt-12">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const next = Math.max(currentPage - 1, 1);
                setCurrentPage(next);
                setSearchParams({ page: next.toString() });
              }}
              disabled={currentPage === 1 || isLoading}
              className="rounded-xl"
            >
              Previous
            </Button>

            <span className="text-sm font-medium text-muted-foreground">
              Page {currentPage} of {Math.ceil(totalCount / limit)}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const next = currentPage + 1;
                setCurrentPage(next);
                setSearchParams({ page: next.toString() });
              }}
              disabled={currentPage >= Math.ceil(totalCount / limit) || isLoading}
              className="rounded-xl"
            >
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Videos;